import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, model, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BaseSiteService, GlobalMessageService, GlobalMessageType } from '@spartacus/core';
import { first, of, Subscription, timer } from 'rxjs';
import { map, take, takeWhile } from 'rxjs/operators';
import { DataLayerService } from 'src/app/shared/services/data-layer.service';
import { MultiFactorAuthService } from 'src/app/shared/services/multi-factor-auth.service';
import * as _ from 'underscore';
import { OtpModalAuthMethod } from '../model/otp-modal.model';
import { OTP_METHODS } from '../../../config/app.config';
import { EventStep, GA_OTP_EVENTS, OtpEventConfig } from '../../../config/ga.config';

@Component({
  selector: 'app-otp-modal',
  templateUrl: './otp-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OtpModalComponent implements AfterContentInit {
  private readonly mfaService = inject(MultiFactorAuthService);
  private readonly otpModal = inject(NgbActiveModal);
  private readonly baseSiteService = inject(BaseSiteService);
  private readonly globalMessageService = inject(GlobalMessageService);
  private readonly dataLayer = inject(DataLayerService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly OTP_METHODS = OTP_METHODS;

  otpChoices = input.required<any>();
  isLimitReached = model<boolean>(false);
  mfa = input<any>({});
  formError = signal(false);
  disableContinue = signal(false);
  disableRequestNew = signal(false);
  requestNewClicked = signal(false);
  timeRemaining = of(0);
  timedOut = signal(false);
  otpForm: FormGroup;
  otpControl: any;
  subscription: Subscription;
  baseSiteId: string;
  counter = 0;
  timerNotif = signal(false);
  methods: OtpModalAuthMethod[] = [];
  chooseVerificationStep = signal(true);
  selectedMethod = signal<string>(null);
  otpContent = signal(null);
  disableChoice = signal(false);

  constructor() {
    this.otpForm = new FormGroup({
      input1: new FormControl('', Validators.required),
      input2: new FormControl('', Validators.required),
      input3: new FormControl('', Validators.required),
      input4: new FormControl('', Validators.required),
      input5: new FormControl('', Validators.required),
      input6: new FormControl('', Validators.required),
    });
    this.baseSiteService.getActive().pipe(take(1)).subscribe((res) => {
      this.baseSiteId = res
   ;
      });
  }

  ngAfterContentInit(): void {
    if (this.isLimitReached()) {
      this.dataLayer.otpEvent(
        this.getGaEvent(EventStep.FORM_ERROR, null),
        'attempt limit reached'
      );
      return;
    }

    this.dataLayer.otpEvent(this.getGaEvent(EventStep.FORM_START));

    const directMethod = this.otpChoices()?.directMethod;
    if (directMethod) {
      this.selectedMethod.set(directMethod);
      this.continueFirstStep();
    }
  }

  continue(): void {
    this.otpControl = this.otpForm.controls;
    this.disableContinue.set(true);
    this.otpForm.disable();
    this.triggerDisable('continue');

    const otp =
      this.otpControl.input1?.value +
      this.otpControl.input2?.value +
      this.otpControl.input3?.value +
      this.otpControl.input4?.value +
      this.otpControl.input5?.value +
      this.otpControl.input6?.value;

    this.mfaService.validateOTP(otp, this.selectedMethod())
      .pipe(
        first(res => res === 'Valid')
      )
      .subscribe({
       next: res => {
         this.dataLayer.otpEvent(this.getGaEvent(EventStep.FORM_SUBMIT, this.selectedMethod()));
         this.otpModal.close({ status: 'success', process: this.otpChoices()?.process });
       },
       error: error => {
         if (error.error !== 'Invalid') {
           this.dataLayer.otpEvent(this.getGaEvent(EventStep.FORM_ERROR, this.selectedMethod()), error.error);
           return;
         }

         this.dataLayer.otpEvent(this.getGaEvent(EventStep.FORM_ERROR, this.selectedMethod()), 'wrong code');
         this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_ERROR);
         this.counter++;
         if (this.counter === 3) {
           this.formError.set(false);
           this.triggerDisable('3attempts');
         } else {
           this.otpForm.enable();
           this.otpForm.reset();
           this.formError.set(true);
         }
       }
    });
  }

  requestNew(params?, resend?) {
    if (this.otpContent()?.limited && this.otpContent()?.maxStep === this.otpContent()?.showStep) {
      this.triggerDisable('limitReached');
    }
    if (!!this.selectedMethod() || this.otpContent()?.maxStep > this.otpContent()?.showStep - 1) {
      this.formError.set(false);

      if (params === 'reset') {
        this.triggerDisable('requestNew');
      }

      this.otpForm.reset();
      this.mfaService.requestNewOTP(this.selectedMethod())
        .pipe(
          first(res => !_.isEmpty(res))
        )
        .subscribe({
          next: (res: any) => {
            this.chooseVerificationStep.set(false);

            if (res.success) {
              this.otpContent.set(res);
            } else {
              this.triggerDisable('limitReached', true);
            }

            this.triggerTimer();
            this.otpForm.enable();
            this.counter = 0;
          },
          error: () => {
            this.triggerDisable('limitReached');
          }
        });
    }
  }

  triggerTimer() {
    this.timedOut.set(false);
    const currentTime = Date.now();

    const milliseconds = (Number.parseFloat(this.otpContent()?.expirationTime) - currentTime) > this.otpContent().milliseconds && !_.isEmpty(this.otpContent().milliseconds) ?
      this.otpContent().milliseconds : (Number.parseFloat(this.otpContent()?.expirationTime) - currentTime);
    this.timeRemaining = timer(0, 1000).pipe(
        map((n) => (milliseconds / 1000 - n) * 1000),
        takeWhile((n) => n >= 0)
    );
    this.triggerDisable('timedOut');
  }

  populateForm(data, inputNumber) {
    let x = 0;
    for (let i = inputNumber; i <= 6; i++) {
      const inpt = `input${ i }`;
      this.otpForm.controls[`${ inpt }`].setValue(data[x]);
      x++;
    }
  }

  closeModal() {
    this.otpModal.dismiss();
  }

  inputTriggers(type, event, inputNumber ?) {
    switch (type) {
      case 'paste':
        const data = event.clipboardData.getData('text').split('');
        this.populateForm(data, inputNumber);
        break;

      case 'delete':
        if (event.keyCode == 8 || event.keyCode == 46) {
          if (_.isEmpty(this.otpForm.controls[`input${ inputNumber }`].value)) {
            if (inputNumber != 1) {
              document
                .querySelectorAll<HTMLInputElement>(
                  `#input${inputNumber - 1}`
                )[0]
                .focus();
            }
          } else {
            this.otpForm.controls[`input${ inputNumber }`].setValue('');
          }
        }
        break;
    }
  }

  triggerDisable(params, type?) {
    switch (params) {
      case 'continue':
        this.disableContinue.set(true);
        this.otpForm.disable();
        this.formError.set(false);
        this.requestNewClicked.set(false);
        this.timedOut.set(false);
        setTimeout(() => {
          this.disableContinue.set(false);
          if (!this.timedOut) { this.otpForm.enable(); }
        }, 3000);
        break;

      case 'requestNew':
        this.disableRequestNew.set(true);
        this.requestNewClicked.set(true);
        this.timerNotif.set(true);
        setTimeout(() => {
          this.disableRequestNew.set(false);
          this.timerNotif.set(false);
        }, 30000);
        break;

      case 'timedOut':
        this.subscription?.unsubscribe();
        this.subscription = this.timeRemaining.subscribe((x) => {
          if (x < 1000) {
            this.timedOut.set(true);
            this.otpForm.disable();
            this.cdr.detectChanges();
          }
        });
        break;

      case '3attempts':
        this.timedOut.set(true);
        break;

      case 'limitReached':
        this.chooseVerificationStep.set(false);
        this.otpContent.set({...this.otpContent(), isLimitReached: true});
        this.isLimitReached.set(true);
        break;

      case 'choice':
        this.disableChoice.set(true);
    }
  }

  dataLayerCreate(event, errorType ?) {
    const baseData: any = {
      event,
      interaction_type: 'authentication',
      process_type: this.otpChoices().gaProcess,
      method: this.selectedMethod() ? this.selectedMethod() : '',
    };

    if (errorType) {
      baseData.error_type = errorType;
    }

    this.dataLayer.viewOTPDataLayer(baseData);
  }

  continueFirstStep() {
    this.chooseVerificationStep.set(false);
    this.requestNew(null);
    this.triggerDisable('choice');
  }

  private getGaEvent(step: EventStep, method: string = null): OtpEventConfig {
    return {
      ...GA_OTP_EVENTS.get(step),
      name: this.otpChoices().process,
      method
    };
  }
}
